import type { CarrierTrackingView } from '@wms/domain';
import { carrierLabel } from '../format';
import { ago, carrierShort, carrierStateLine, trackingOf, type Tracked } from '../tracking';
import type { OrderSummary } from '../types';
import { Icon } from './icons';

// Kargo takibi in the order screens (helpers in ../tracking.ts).

/** A list cell: the tracking number, a link when the carrier has a page for it. */
export function TrackingNumber({ order }: { order: Tracked }) {
  const { number, url } = trackingOf(order);
  if (!number) return <span className="mono">—</span>;
  if (!url) return <span className="mono">{number}</span>;
  return (
    // a row opens its order: the link opens only the carrier's page
    <a className="mono trk-link" href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title="Kargo firmasının takip sayfasında açar">
      {number}
    </a>
  );
}

/** The carrier's last word on the parcel, one small line; a return in warning colours. */
export function CarrierState({ tracking }: { tracking: CarrierTrackingView | null | undefined }) {
  const text = carrierStateLine(tracking);
  if (!text) return null;
  return <div className={`trk-state${tracking?.returning ? ' back' : ''}`}>{text}</div>;
}

/** An order's dialog: the number with the carrier's page, the parcel's state, and what to do with a return. */
export function TrackingDetail({ order, staff }: { order: Tracked & Pick<OrderSummary, 'carrierCode'>; staff: boolean }) {
  const ct = order.carrierTracking ?? null;
  const { number, url } = trackingOf(order);
  if (!number && !ct) return null;
  const state = carrierStateLine(ct);
  // the carrier's own number, when the order keeps another one (the label's code)
  const own = ct?.trackingNumber && ct.trackingNumber !== number ? ct.trackingNumber : null;
  return (
    <div className="trk-box">
      {number && (
        <div className="trk-line">
          <span>
            Takip no: <b className="mono">{number}</b>
            {order.carrierCode && <span className="muted"> · {carrierLabel(order.carrierCode)}</span>}
          </span>
          {url && (
            <a className="btn sm" href={url} target="_blank" rel="noopener noreferrer">
              <Icon name="link" size={16} /> Takip sayfası
            </a>
          )}
        </div>
      )}
      {own && ct && (
        <div className="trk-line">
          <span>
            {carrierShort(ct.carrierCode)} takip no: <b className="mono">{own}</b>
          </span>
          {ct.trackingUrl && (
            <a className="btn sm" href={ct.trackingUrl} target="_blank" rel="noopener noreferrer">
              <Icon name="link" size={16} /> Takip sayfası
            </a>
          )}
        </div>
      )}
      {ct && (
        <div className={`trk-state${ct.returning ? ' back' : ''}`}>
          {state ?? `${carrierShort(ct.carrierCode)}: kayıt bulundu`}
          <span className="muted"> · son bakış {ago(ct.seenAt)}</span>
        </div>
      )}
      {ct?.returning && (
        <div className="banner warn">
          {staff ? 'Kargo paketi geri getiriyor: paket gelince iade masasında kabul edin.' : 'Kargo paketi depoya geri getiriyor; depo paketi gelince iade olarak kabul eder.'}
        </div>
      )}
      {staff && ct?.warning && <div className="banner warn">{ct.warning}</div>}
    </div>
  );
}
